import { useEffect, useRef, useState } from "react";
import { i18n, initializeI18n } from "../localization/i18n";
import { translate } from "../localization/translateService";
import { getShorts } from "../services/shortsService";

// "Free Korean learning shorts" — horizontal scroll-snap carousel of 9:16 cards
// with prev/next arrows + pagination dots (Figma). CMS-driven: title / subtitle +
// `shorts` ({ title, image, link }). prefers-reduced-motion honored by smooth-scroll.
// Localization: title/subtitle/shorts text = CMS props (Umbraco variant). When CMS empty -> i18n
// translate() defaults (NOT hardcoded English). View counts stay as fixed numeric values.
const DEFAULT_SHORT_VIEWS = ["14,235", "344,276", "8,458", "1,079", "344,276"];
const HOME_SHORTS_COUNT = 10; // number of latest shorts to show in the home carousel

const Chevron = ({ dir }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d={dir === "prev" ? "M15 6l-6 6 6 6" : "M9 6l6 6-6 6"} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function FreeShortsSection({ props }) {
  const [lang, setLang] = useState(null);
  const [t, setT] = useState({});
  // Latest published shorts from the Shorts API — same data as portals/admin/shorts-management,
  // ordered by createdTime desc (the API default when no seed/sort is passed).
  const [apiItems, setApiItems] = useState(null);

  // Init i18n + track current language (re-translate on language change)
  useEffect(() => {
    if (!i18n.isInitialized) {
      initializeI18n().then(() => setLang(i18n.language));
    } else {
      setLang(i18n.language);
    }
  }, []);

  // Fetch the latest shorts once on mount (createdTime desc). On empty/error, leave [] so
  // the section gracefully falls back to CMS props / i18n defaults.
  useEffect(() => {
    let active = true;
    getShorts(1, HOME_SHORTS_COUNT)
      .then((res) => { if (active) setApiItems(Array.isArray(res?.items) ? res.items : []); })
      .catch(() => { if (active) setApiItems([]); });
    return () => { active = false; };
  }, []);

  // i18n defaults — used only when the CMS field is empty (no hardcoded English in markup)
  useEffect(() => {
    let active = true;
    (async () => {
      const out = {};
      out.title = await translate("UMBRACO_LANDING.HOME.SHORTS.TITLE", false, "Free Korean learning shorts");
      out.subtitle = await translate("UMBRACO_LANDING.HOME.SHORTS.SUBTITLE", false, "Explore short videos for TOPIK tips, Korean expressions, and study motivation.");
      out.cardTitle = await translate("UMBRACO_LANDING.HOME.SHORTS.CARD_TITLE", false, "Daily Korean Phrases You'll Actually Use");
      out.caption1 = await translate("UMBRACO_LANDING.HOME.SHORTS.CAPTION1", false, "Koreans — We don't say He / She / They");
      out.caption2 = await translate("UMBRACO_LANDING.HOME.SHORTS.CAPTION2", false, "Can I have a cup of coffee?");
      out.caption3 = await translate("UMBRACO_LANDING.HOME.SHORTS.CAPTION3", false, "How to introduce YOURSELF");
      out.caption4 = await translate("UMBRACO_LANDING.HOME.SHORTS.CAPTION4", false, "Me too");
      out.caption5 = await translate("UMBRACO_LANDING.HOME.SHORTS.CAPTION5", false, "Can I have a cup of coffee?");
      if (active) setT(out);
    })();
    return () => { active = false; };
  }, [lang]);

  // CMS first (Umbraco variant); i18n default when CMS empty
  const title = t.title || props?.shortsTitle || "";
  const subtitle = t.subtitle || props?.shortsSubtitle || "";

  const i18nShorts = [
    { caption: t.caption1, title: t.cardTitle, views: DEFAULT_SHORT_VIEWS[0] },
    { caption: t.caption2, title: t.cardTitle, views: DEFAULT_SHORT_VIEWS[1] },
    { caption: t.caption3, title: t.cardTitle, views: DEFAULT_SHORT_VIEWS[2] },
    { caption: t.caption4, title: t.cardTitle, views: DEFAULT_SHORT_VIEWS[3] },
    { caption: t.caption5, title: t.cardTitle, views: DEFAULT_SHORT_VIEWS[4] },
  ];

  // Real shorts from the API (createdTime desc) mapped to the card shape; each links to the
  // /shorts player for that short. Falls back to CMS props, then i18n defaults.
  const langSeg = lang || i18n.language || "en";
  const apiShorts = (apiItems || []).map((d) => ({
    // Real shorts have a single title; render it via short-card__title (the bold
    // white overlay in the Figma). short-card__cap is display:none, so mapping the
    // title to `caption` left the card text-less.
    title: d.title,
    image: d.thumbnailUrl,
    views: typeof d.viewCount === "number" ? d.viewCount.toLocaleString() : "",
    link: `/${langSeg}/shorts?shortsId=${d.publicId}`,
  }));

  const shorts =
    apiShorts.length > 0
      ? apiShorts
      : Array.isArray(props?.shorts) && props.shorts.length > 0
        ? props.shorts
        : i18nShorts;

  const rowRef = useRef(null);
  const [active, setActive] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [edges, setEdges] = useState({ atStart: true, atEnd: false });

  // Exact per-card step (card width + gap) measured from real DOM positions, so the
  // dots track the scroll regardless of the CSS gap value.
  const step = () => {
    const row = rowRef.current;
    const cards = row ? row.querySelectorAll(".short-card") : null;
    if (cards && cards.length >= 2) return cards[1].offsetLeft - cards[0].offsetLeft;
    if (cards && cards.length === 1) return cards[0].offsetWidth;
    return 260;
  };
  // Sync the active dot + edge flags (used to hide the prev/next arrow at each end).
  const update = () => {
    const row = rowRef.current;
    if (!row) return;
    const max = row.scrollWidth - row.clientWidth;
    const s = step() || 1;
    // # of dots = reachable card-step stops to the end (NOT one per card), so the
    // dots progress logically and the last dot lands exactly at the end.
    const maxIdx = max > 1 ? Math.ceil(max / s - 0.02) : 0;
    const atStart = row.scrollLeft <= 2;
    const atEnd = row.scrollLeft >= max - 2;
    setPageCount(maxIdx + 1);
    setEdges({ atStart, atEnd });
    setActive(atEnd ? maxIdx : Math.max(0, Math.min(maxIdx, Math.round(row.scrollLeft / s))));
  };
  const scrollByCards = (dir) => {
    const row = rowRef.current;
    if (row) row.scrollBy({ left: dir * step(), behavior: "smooth" });
  };
  const goTo = (i) => {
    const row = rowRef.current;
    if (row) row.scrollTo({ left: Math.min(i * step(), row.scrollWidth - row.clientWidth), behavior: "smooth" });
  };

  // Init + keep edges/active correct on resize (and after data loads).
  useEffect(() => {
    const row = rowRef.current;
    if (row && shorts.length > 1 && row.scrollLeft <= 2 && window.matchMedia("(max-width: 768px)").matches) {
      row.scrollLeft = step();
    }
    update();
    const onResize = () => update();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shorts.length]);

  return (
    <section className="shorts-section">
      <div className="shorts-section__inner">
        <div className="shorts-section__head">
          <h2 className="shorts-section__title animate-on-scroll">{title}</h2>
          <p className="shorts-section__subtitle animate-on-scroll">{subtitle}</p>
        </div>

        <div className="shorts-carousel">
          <button type="button" className="shorts-nav shorts-nav--prev" aria-label="Previous" onClick={() => scrollByCards(-1)}
            style={{ visibility: edges.atStart ? "hidden" : "visible", pointerEvents: edges.atStart ? "none" : "auto" }}>
            <Chevron dir="prev" />
          </button>

          <div className="shorts-row" ref={rowRef} onScroll={update}>
            {shorts.map((s, i) => {
              const Tag = s.link ? "a" : "div";
              return (
                <Tag className="short-card" key={i} {...(s.link ? { href: s.link } : {})}>
                  <div className="short-card__media" style={s.image ? { backgroundImage: `url(${s.image})` } : undefined}>
                    {!s.image && (
                      <svg viewBox="0 0 24 24" width="34" height="34" aria-hidden="true">
                        <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" strokeWidth="1.6" />
                        <path d="M10 8.5l6 3.5-6 3.5z" fill="currentColor" />
                      </svg>
                    )}
                    {s.caption && <p className="short-card__cap">{s.caption}</p>}
                    <div className="short-card__bot">
                      {s.title && <b className="short-card__title">{s.title}</b>}
                      {s.views != null && s.views !== "" && (
                        <span className="short-card__views">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                            <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z" /><circle cx="12" cy="12" r="3" />
                          </svg>
                          {s.views}
                        </span>
                      )}
                    </div>
                  </div>
                </Tag>
              );
            })}
          </div>

          <button type="button" className="shorts-nav shorts-nav--next" aria-label="Next" onClick={() => scrollByCards(1)}
            style={{ visibility: edges.atEnd ? "hidden" : "visible", pointerEvents: edges.atEnd ? "none" : "auto" }}>
            <Chevron dir="next" />
          </button>
        </div>

        {pageCount > 1 && (
          <div className="shorts-dots" role="tablist" aria-label="Shorts pages">
            {Array.from({ length: pageCount }, (_, i) => (
              <button
                key={i}
                type="button"
                className={"shorts-dot" + (i === active ? " is-active" : "")}
                aria-label={`Go to page ${i + 1}`}
                onClick={() => goTo(i)}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
